import React, { useState, useEffect } from "react";

import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";

import { useTheme, styled } from "@mui/material/styles";
import {
  CircularProgress,
  Box,
  Button,
  Typography,
  Stack,
} from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import ModalContainer from "components/ModalContainer";
import TwoColumnContainer from "components/Container/TwoColumnContainer";
import { MenuSelect } from "components/Select/menuSelect";
import AutocompleteSelect from "components/Select/AutocompleteSelect";
import InputField from "components/InputField";
import TextAreaField from "components/TextAreaField";

import { ReactComponent as UnverifiedIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/icn_table_cell_Add.svg";
import { ReactComponent as AddHoverIcon } from "assets/images/icons/icn_table_cell_Add_hover.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import { getCountryList, getTimezoneList } from "utils/timezone";
import { validateDeviceAccount, validateDeviceAccountPassword } from 'utils/validate';

const labelStyle = (theme) => ({
  color: theme.palette.primary.greyPrimary,
  fontSize: theme.typography.customFontSize[2],
});

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: "30px",
  marginLeft: "20px",
  color: theme.palette.primary.main,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.customFontSize[5],
  fontWeight: theme.typography.customFontWeight[3],
}));

const StyledRowBox = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  gap: 2,
  alignItems: "center",
}));

const StyledIconButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.transparent,
  width: "22px",
  height: "22px",
  minWidth: "16px",
  padding: "0",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.transparent,
  },
}));

const ModalForAddOrgOrSite = ({ auth, display, title, setOpenModal, cancelBtn, saveBtn, errorHandling, refresh, createStandalone: standaloneMode }) => {
  const theme = useTheme();
  const labelTheme = labelStyle(theme)

  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [name, setName] = useState('');
  const [selectCountryList, setSelectCountryList] = useState([]);
  const [selectCountry, setSelectCountry] = useState(null);
  const [timezoneList, setTimezoneList] = useState([]);
  const [selectTimezoneData, setSelectTimezoneData] = useState(null);
  const [ntpServers, setNtpServers] = useState(['ntp1.nuclias.com']);
  const [addressData, setAddressData] = useState("")
  const [userName, setUserName] = useState('');
  const [userPW, setUserPW] = useState('');

  const [userError, setUserError] = useState("");
  const [userPWError, setUserPWError] = useState("");

  const [addOrgOrSiteUuid, _setAddOrgOrSiteUuid] = useState(uuidv4());

  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const handleInput = (value, key) => {
    switch (key) {
      case "name":
        setName(value)
        break;
      case "userName":
        setUserName(value)
        const userErrorResult = validateDeviceAccount(value);
        setUserError(userErrorResult.valid ? "" : userErrorResult.msg);
        break;
      case "userPW":
        setUserPW(value)
        const pwErrorResult = validateDeviceAccountPassword(value, userName);
        setUserPWError(pwErrorResult.valid ? "" : pwErrorResult.msg);
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (display === true) {
      const currentSelectCountryList = getCountryList();
      setSelectCountryList(currentSelectCountryList)
    }
  }, [display])

  function handleAddNtpServer() {
    if (ntpServers.length < 3) {
      setNtpServers([...ntpServers, ""]);
    }
  }

  function handleDeleteNtpServer(index) {
    const updated = ntpServers.filter((_, i) => i !== index);
    setNtpServers(updated);
  }

  function handleChangeNtpServer(value, index) {
    const updated = [...ntpServers];
    updated[index] = value;
    setNtpServers(updated);
  }

  function handleClose() {
    setIsLoading(false)
    setName('');
    setSelectCountryList([]);
    setSelectCountry(null);
    setTimezoneList([]);
    setSelectTimezoneData(null);
    setNtpServers(['ntp1.nuclias.com']);
    setUserName('');
    setUserPW('');
    setUserError('')
    setUserPWError('');
    setOpenModal(false);
  };

  function handleSaveBtn() {
    setIsLoading(true)
    const tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    if (auth === 'Organization') {
      const createStandalone = standaloneMode ?? (
        Object.keys(tmpSelectedBread.realm || {}).length === 0
        && Object.keys(tmpSelectedBread.orgs || {}).length === 0
        && Object.keys(tmpSelectedBread.site || {}).length === 0
      );
      const apiUrl = `${createStandalone ? "" : "/ents/:entId"}/orgs.create`;

      const restfulParams = createStandalone ? null : { entId: tmpSelectedBread.realm.id }

      const requestParams = {
        data: {
          "orgName": name,
          countryDomain: {
            "countryIndex": selectCountry.value
          },
          timeConfig: {
            "timezone": selectTimezoneData.value,
            "ntpServer1": ntpServers[0] || "",
            "ntpServer2": ntpServers[1] || null,
            "ntpServer3": ntpServers[2] || null,
          },
          deviceAccount: {
            "username": userName,
            "password": userPW
          }
        }
      }

      sendRequest(apiUrl, addOrgOrSiteUuid, restfulParams, requestParams);
    }
    if (auth === 'Site') {
      const standalone = !tmpSelectedBread.orgs.id || Object.keys(tmpSelectedBread.orgs).length === 0;
      const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites.create`;

      const restfulParamsConfig = {
        standalone: {
          orgId: tmpSelectedBread.realm.id,
        },
        nonStandalone: {
          entId: tmpSelectedBread.realm.id,
          orgId: tmpSelectedBread.orgs.id
        },
      };

      const mode = standalone ? "standalone" : "nonStandalone";
      const restfulParams = restfulParamsConfig[mode];

      const requestParams = {
        data: {
          "name": name,
          "address": addressData,
          countryDomain: {
            "countryIndex": selectCountry.value
          },
          timeConfig: {
            "timezone": selectTimezoneData.value,
            "ntpServer1": ntpServers[0] || "",
            "ntpServer2": ntpServers[1] || null,
            "ntpServer3": ntpServers[2] || null,
          },
          deviceAccount: {
            "username": userName,
            "password": userPW
          }
        }
      }

      sendRequest(apiUrl, addOrgOrSiteUuid, restfulParams, requestParams);
    }
  }

  useEffect(() => {
    const response = getData(addOrgOrSiteUuid);
    if (response) {
      handleClose()
      if (response.code) return errorHandling(response.message)
      refresh()
    }
  }, [dataMap])

  useEffect(() => {
    if (!selectCountry?.value) {
      setTimezoneList([]);
      setSelectTimezoneData(null);
      return;
    }

    const timezoneList = getTimezoneList(selectCountry.value);
    setTimezoneList(timezoneList);
    setSelectTimezoneData(timezoneList[0] || null);
  }, [selectCountry])

  return (
    <>
      <ModalContainer display={display} modalWidth={isLoading ? 'loading' : 'auto'}>
        {
          isLoading ?
            <CircularProgress /> :
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <StyledTitle>
                  {title}
                </StyledTitle>
                <Button
                  onClick={handleClose}
                  sx={{
                    marginRight: "-20px",
                    marginTop: "-35px",
                    backgroundColor: theme.palette.primary.transparent,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.transparent,
                    },
                  }}
                >
                  <CloseIcon />
                </Button>
              </Box>
              <Stack spacing={2} sx={{ ml: "40px" }}>
                <Box flex={1}>
                  <TwoColumnContainer
                    gridDistance={[3, 4.6]}
                    labelStyle={labelTheme}
                    label={`${auth} Name`}
                    required={auth === "Organization"}
                  >
                    <InputField
                      value={name}
                      onChange={(value) => handleInput(value, 'name')}
                      autoComplete='off'
                    />
                  </TwoColumnContainer>
                </Box>
                <Box flex={1}>
                  <TwoColumnContainer gridDistance={[3.1, 6.7]} labelStyle={labelTheme} label="Select Country" required>
                    <StyledRowBox>
                      <AutocompleteSelect
                        width="65%"
                        placeholder="Select a Country"
                        options={selectCountryList || []}
                        value={selectCountry}
                        setValue={setSelectCountry}
                      />
                    </StyledRowBox>
                  </TwoColumnContainer>
                </Box>
                <Box flex={1}>
                  <TwoColumnContainer gridDistance={[3.1, 6.7]} labelStyle={labelTheme} label="Time Zone" required>
                    <StyledRowBox>
                      <MenuSelect
                        width="65%"
                        placeholder="Select a Time Zone"
                        options={timezoneList || []}
                        value={selectTimezoneData?.value || ""}
                        onChange={setSelectTimezoneData}
                      />
                    </StyledRowBox>
                  </TwoColumnContainer>
                </Box>
                <Box flex={1}>
                  <TwoColumnContainer
                    gridDistance={[3, 4.6]}
                    labelStyle={labelTheme}
                    label="NTP Server"
                    required
                  >
                    <InputField
                      value={ntpServers[0]}
                      onChange={(value) => handleChangeNtpServer(value, 0)}
                      autoComplete='off'
                    />
                  </TwoColumnContainer>
                </Box>
                {ntpServers.map((value, index) => {
                  if (index === 0) return null;

                  return (
                    <Box flex={1} key={index}>
                      <TwoColumnContainer
                        gridDistance={[3, 4.6]}
                        labelStyle={labelTheme}
                      >
                        <StyledRowBox>
                          <Box sx={{ flex: 1 }}>
                            <InputField
                              value={value}
                              onChange={(value) => handleChangeNtpServer(value, index)}
                              autoComplete='off'
                            />
                          </Box>
                          <StyledIconButton
                            onClick={() => handleDeleteNtpServer(index)}
                          >
                            <UnverifiedIcon />
                          </StyledIconButton>
                        </StyledRowBox>
                      </TwoColumnContainer>
                    </Box>
                  );
                })}
                <Box flex={1}>
                  <TwoColumnContainer gridDistance={[3, 4.5]} labelStyle={labelTheme}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                        alignItems: "center",
                        justifyContent: "flex-end"
                      }}
                    >
                      <OutlineRectangleButton
                        onMouseEnter={() => setIsAddHovered(true)}
                        onMouseLeave={() => setIsAddHovered(false)}
                        startIcon={
                          isAddHovered ? <AddHoverIcon /> : <AddIcon />
                        }
                        width="54px"
                        height="28px"
                        onClick={handleAddNtpServer}
                        disabled={ntpServers.length >= 3}
                      >
                        Add
                      </OutlineRectangleButton>
                    </Box>
                  </TwoColumnContainer>
                </Box>
                {
                  auth === 'Site' && <Box flex={1}>
                    <TwoColumnContainer containerAlignItems="flex-start" gridDistance={[3.1, 7.5]} labelStyle={labelTheme} label="Address">
                      <StyledRowBox>
                        <TextAreaField
                          value={addressData}
                          onChange={setAddressData}
                          placeholder="Apply to all managed device"
                          rows={10}
                        />
                      </StyledRowBox>
                    </TwoColumnContainer>
                  </Box>
                }
              </Stack>
              <StyledRowBox>
                <StyledTitle>
                  {auth === 'Organization' && "Inventory"} Device Account
                </StyledTitle>
              </StyledRowBox>
              <Stack spacing={2} sx={{ ml: "40px" }}>
                <Box flex={1}>
                  <TwoColumnContainer gridDistance={[3, 4.6]} labelStyle={labelTheme} label="Username" required>
                    <InputField
                      value={userName}
                      isValid={!userError}
                      onChange={(value) => handleInput(value, 'userName')}
                      autoComplete='off'
                    />
                  </TwoColumnContainer>
                </Box>
                <Box flex={1}>
                  <TwoColumnContainer gridDistance={[3, 4.6]} labelStyle={labelTheme} label="Password" required>
                    <InputField
                      type="password"
                      value={userPW}
                      onChange={(value) => handleInput(value, 'userPW')}
                      isValid={!userPWError}
                      errorHelperText={userPWError}
                      autoComplete='off'
                    />
                  </TwoColumnContainer>
                </Box>
              </Stack>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 5, mb: 2 }}>
                <OutlineButton
                  onClick={handleClose}
                  width="100px"
                  height="32px"
                  sx={{ mr: 2 }}
                >
                  {cancelBtn}
                </OutlineButton>
                <SolidButton
                  onClick={handleSaveBtn}
                  width="100px"
                  height="32px"
                  disabled={
                    (auth === "Organization" && !name.trim()) ||
                    !ntpServers[0]?.trim() ||
                    !userName ||
                    userError ||
                    !userPW ||
                    userPWError ||
                    !selectCountry ||
                    !selectTimezoneData
                  }
                >
                  {saveBtn}
                </SolidButton>
              </Box >
            </>
        }
      </ModalContainer>
    </>
  );
};

export default ModalForAddOrgOrSite;
